import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';
import {
  SearchIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationCircleIcon,
  ClockIcon,
  ArrowLeftIcon,
  DocumentTextIcon,
  UserIcon,
  IdentificationIcon,
  MailIcon,
  PhoneIcon,
  CurrencyDollarIcon,
  CalendarIcon,
  LocationMarkerIcon,
  ShieldCheckIcon,
  ClipboardListIcon
} from '@heroicons/react/outline';
import toast from 'react-hot-toast';
import moment from 'moment';

export default function Claims() {
  const [claims, setClaims] = useState({ claims: [], total: 0, totalPages: 0, currentPage: 1 });
  const [loading, setLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState('');
  const { user } = useAuth();
  const { t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const queryParams = new URLSearchParams(location.search);
  const policyId = queryParams.get('policyId');
  const isNewClaim = location.pathname === '/claims/new';
  const isViewClaim = params.id && !isNewClaim;
  const [claimDetails, setClaimDetails] = useState(null);
  const [clientDetailClientId, setClientDetailClientId] = useState(null);

  // For new claim form
  const [newClaimData, setNewClaimData] = useState({
    policyId: policyId || '',
    outstandingAmount: '',
    description: '',
    informedDate: moment().format('YYYY-MM-DD'),
    accidentDate: moment().format('YYYY-MM-DD'),
  });
  const [policies, setPolicies] = useState([]);
  const [loadingPolicies, setLoadingPolicies] = useState(false);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);

  useEffect(() => {
    if (isNewClaim) {
      fetchPolicies();
      // If a policy ID is provided in the URL, load that policy's details
      if (policyId) {
        fetchPolicyDetails(policyId);
      }
    } else if (isViewClaim) {
      fetchClaimDetails(params.id);
    } else {
      fetchClaims();
    }
  }, [isNewClaim, isViewClaim, selectedStatus, location.search, params.id]);

  const fetchClaimDetails = async (claimId) => {
    try {
      setLoading(true);
      const response = await api.get(`/api/claims/${claimId}`);
      setClaimDetails(response.data);
    } catch (error) {
      console.error('Error fetching claim details:', error);
      toast.error(t('claims.claimUpdateFailed'));
      navigate('/claims');
    } finally {
      setLoading(false);
    }
  };

  const fetchClaims = async (page = 1) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('page', page);
      if (selectedStatus) {
        params.append('status', selectedStatus);
      }

      const response = await api.get(`/api/claims?${params.toString()}`);
      setClaims(response.data);
    } catch (error) {
      console.error('Error fetching claims:', error);
      toast.error(t('claims.claimUpdateFailed'));
    } finally {
      setLoading(false);
    }
  };

  const fetchPolicies = async () => {
    if (!isNewClaim) return;
    
    try {
      setLoadingPolicies(true);
      // Get only paid policies as those are the ones that can have claims
      const response = await api.get('/api/policies?paymentStatus=paid');
      setPolicies(response.data.policies || []);
    } catch (error) {
      console.error('Error fetching policies:', error);
      toast.error(t('claims.claimCreationFailed'));
    } finally {
      setLoadingPolicies(false);
    }
  };

  const fetchPolicyDetails = async (id) => {
    try {
      const response = await api.get(`/api/policies/${id}`);
      const policy = response.data;
      setSelectedPolicy(policy);
      
      if (policy.client) {
        setSelectedClient(policy.client);
        // Store client ID for back button
        setClientDetailClientId(policy.client.id);
      }
      
      // Pre-set the policy ID in the form
      setNewClaimData(prev => ({
        ...prev,
        policyId: policy.id
      }));
    } catch (error) {
      console.error('Error fetching policy details:', error);
      toast.error(t('claims.claimCreationFailed'));
    }
  };

  const handlePolicyChange = async (e) => {
    const policyId = e.target.value;
    setNewClaimData({...newClaimData, policyId});
    
    if (policyId) {
      await fetchPolicyDetails(policyId);
    } else {
      setSelectedPolicy(null);
      setSelectedClient(null);
    }
  };

  const handleCreateClaim = async (e) => {
    e.preventDefault();
    
    try {
      await api.post('/api/claims', newClaimData);
      toast.success(t('claims.claimCreated'));
      navigate('/claims');
    } catch (error) {
      console.error('Error creating claim:', error);
      toast.error(error.response?.data?.message || t('claims.claimCreationFailed'));
    }
  };

  const handleUpdateClaimStatus = async (claimId, newStatus, settlementData = null) => {
    try {
      const data = { status: newStatus };
      
      if (newStatus === 'settled' && settlementData) {
        data.settlementAmount = settlementData.settlementAmount;
        data.settlementDate = settlementData.settlementDate;
      }
      
      await api.put(`/api/claims/${claimId}/status`, data);
      toast.success(t('claims.claimUpdated', { status: newStatus }));
      
      if (isViewClaim) {
        fetchClaimDetails(claimId);
      } else {
        fetchClaims(claims.currentPage);
      }
    } catch (error) {
      console.error('Error updating claim:', error);
      toast.error(t('claims.claimUpdateFailed'));
    }
  };

  const handleStatusFilterChange = (e) => {
    setSelectedStatus(e.target.value);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <ClockIcon className="h-5 w-5 text-yellow-500" />;
      case 'approved':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'rejected':
        return <XCircleIcon className="h-5 w-5 text-red-500" />;
      case 'settled':
        return <CheckCircleIcon className="h-5 w-5 text-blue-500" />;
      default:
        return <ExclamationCircleIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  // Render claim details view
  if (isViewClaim) {
    if (loading) {
      return <div className="flex justify-center items-center h-64">{t('claims.loadingClaimDetails')}</div>;
    }

    if (!claimDetails) {
      return <div className="text-center py-8">{t('claims.noClaimsFound')}</div>;
    }

    const policy = claimDetails.policy;
    const client = claimDetails.client;
    const latestDocument = policy?.documents && policy.documents.length > 0 ? policy.documents[0] : null;

    return (
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <button
            onClick={() => navigate('/claims')}
            className="flex items-center text-sm text-gray-600 hover:text-gray-900"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-1" /> {t('claims.backToClaims')}
          </button>
        </div>

        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
            <div>
              <h3 className="text-lg leading-6 font-medium text-gray-900">{t('claims.claimDetails')}</h3>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">#{claimDetails.claimNumber}</p>
            </div>
            <div className="flex items-center">
              {getStatusIcon(claimDetails.status)}
              <span className="ml-1.5 text-sm font-medium capitalize">
                {t(`claims.${claimDetails.status}`)}
              </span>
            </div>
          </div>

          <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
            <dl className="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2">
              <div className="sm:col-span-1">
                <dt className="text-sm font-medium text-gray-500">{t('claims.outstandingAmount')}</dt>
                <dd className="mt-1 text-sm text-gray-900">LYD {claimDetails.outstandingAmount}</dd>
              </div>
              <div className="sm:col-span-1">
                <dt className="text-sm font-medium text-gray-500">{t('claims.informedDate')}</dt>
                <dd className="mt-1 text-sm text-gray-900">{moment(claimDetails.informedDate).format('MMMM D, YYYY')}</dd>
              </div>
              <div className="sm:col-span-1">
                <dt className="text-sm font-medium text-gray-500">{t('claims.accidentDate')}</dt>
                <dd className="mt-1 text-sm text-gray-900">{moment(claimDetails.accidentDate).format('MMMM D, YYYY')}</dd>
              </div>
              {claimDetails.status === 'settled' && (
                <>
                  <div className="sm:col-span-1">
                    <dt className="text-sm font-medium text-gray-500">{t('claims.settlementAmount')}</dt>
                    <dd className="mt-1 text-sm text-gray-900">LYD {claimDetails.settlementAmount}</dd>
                  </div>
                  <div className="sm:col-span-1">
                    <dt className="text-sm font-medium text-gray-500">{t('claims.settlementDate')}</dt>
                    <dd className="mt-1 text-sm text-gray-900">{moment(claimDetails.settlementDate).format('MMMM D, YYYY')}</dd>
                  </div>
                </>
              )}
              <div className="sm:col-span-2">
                <dt className="text-sm font-medium text-gray-500">{t('claims.description')}</dt>
                <dd className="mt-1 text-sm text-gray-900">{claimDetails.description}</dd>
              </div>
            </dl>
          </div>

          {/* Policy Information Section */}
          <div className="border-t border-gray-200">
            <div className="px-4 py-5 sm:px-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">{t('claims.policyInformation')}</h3>
            </div>
            <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
              <dl className="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2">
                <div className="sm:col-span-1">
                  <dt className="text-sm font-medium text-gray-500 flex items-center">
                    <ShieldCheckIcon className="h-4 w-4 mr-1 text-gray-400" /> {t('claims.policyNumber')}
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">{policy.policyNumber}</dd>
                </div>
                <div className="sm:col-span-1">
                  <dt className="text-sm font-medium text-gray-500 flex items-center">
                    <ClipboardListIcon className="h-4 w-4 mr-1 text-gray-400" /> {t('claims.policyType')}
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">{policy.policyType}</dd>
                </div>
                <div className="sm:col-span-1">
                  <dt className="text-sm font-medium text-gray-500 flex items-center">
                    <CurrencyDollarIcon className="h-4 w-4 mr-1 text-gray-400" /> {t('claims.limitOfIndemnity')}
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">LYD {policy.limitOfIndemnity}</dd>
                </div>
                <div className="sm:col-span-1">
                  <dt className="text-sm font-medium text-gray-500 flex items-center">
                    <CurrencyDollarIcon className="h-4 w-4 mr-1 text-gray-400" /> {t('claims.premium')}
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">LYD {policy.totalPremium}</dd>
                </div>
              </dl>
            </div>
          </div>

          {/* Client Information Section */}
          <div className="border-t border-gray-200">
            <div className="px-4 py-5 sm:px-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">{t('claims.clientInformation')}</h3>
            </div>
            <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
              <dl className="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2">
                <div className="sm:col-span-1">
                  <dt className="text-sm font-medium text-gray-500 flex items-center">
                    <UserIcon className="h-4 w-4 mr-1 text-gray-400" /> {t('claims.clientName')}
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">{client.firstName} {client.lastName}</dd>
                </div>
                <div className="sm:col-span-1">
                  <dt className="text-sm font-medium text-gray-500 flex items-center">
                    <IdentificationIcon className="h-4 w-4 mr-1 text-gray-400" /> {t('claims.clientNumber')}
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">{client.clientNumber}</dd>
                </div>
                <div className="sm:col-span-1">
                  <dt className="text-sm font-medium text-gray-500 flex items-center">
                    <MailIcon className="h-4 w-4 mr-1 text-gray-400" /> {t('common.email')}
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">{client.email}</dd>
                </div>
                <div className="sm:col-span-1">
                  <dt className="text-sm font-medium text-gray-500 flex items-center">
                    <PhoneIcon className="h-4 w-4 mr-1 text-gray-400" /> {t('claims.mobile')}
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">{client.mobile}</dd>
                </div>
                <div className="sm:col-span-2">
                  <dt className="text-sm font-medium text-gray-500 flex items-center">
                    <LocationMarkerIcon className="h-4 w-4 mr-1 text-gray-400" /> {t('common.address')}
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">{client.address}</dd>
                </div>
              </dl>
            </div>
          </div>

          {/* Latest Document Section */}
          {latestDocument && (
            <div className="border-t border-gray-200">
              <div className="px-4 py-5 sm:px-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900">{t('claims.latestDocument')}</h3>
              </div>
              <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
                <dl className="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2">
                  <div className="sm:col-span-1">
                    <dt className="text-sm font-medium text-gray-500 flex items-center">
                      <DocumentTextIcon className="h-4 w-4 mr-1 text-gray-400" /> {t('claims.documentNumber')}
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900">{latestDocument.documentNumber}</dd>
                  </div>
                  <div className="sm:col-span-1">
                    <dt className="text-sm font-medium text-gray-500 flex items-center">
                      <CalendarIcon className="h-4 w-4 mr-1 text-gray-400" /> {t('claims.generationDate')}
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900">{moment(latestDocument.generationDate || latestDocument.createdAt).format('MMMM D, YYYY')}</dd>
                  </div>
                </dl>
              </div>
            </div>
          )}

          {/* Actions Section */}
          {(user.role === 'admin' || user.role === 'company') && (
            <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
              <div className="flex justify-end space-x-3">
                {claimDetails.status === 'pending' && (
                  <>
                    <button
                      onClick={() => handleUpdateClaimStatus(claimDetails.id, 'approved')}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                    >
                      {t('claims.approveClaim')}
                    </button>
                    <button
                      onClick={() => handleUpdateClaimStatus(claimDetails.id, 'rejected')}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    >
                      {t('claims.rejectClaim')}
                    </button>
                  </>
                )}
                
                {claimDetails.status === 'approved' && (
                  <button
                    onClick={() => {
                      const amount = prompt(t('claims.enterSettlementAmount'), claimDetails.outstandingAmount);
                      if (amount) {
                        const today = moment().format('YYYY-MM-DD');
                        handleUpdateClaimStatus(claimDetails.id, 'settled', {
                          settlementAmount: amount,
                          settlementDate: today
                        });
                      }
                    }}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    {t('claims.settleClaim')}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Render new claim form
  if (isNewClaim) {
    return (
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <button
            onClick={() => clientDetailClientId ? navigate(`/client/${clientDetailClientId}`) : navigate('/claims')}
            className="flex items-center text-sm text-gray-600 hover:text-gray-900"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-1" /> 
            {clientDetailClientId ? t('claims.backToClientDetails') : t('claims.backToClaims')}
          </button>
        </div>
        
        <h1 className="text-2xl font-semibold text-gray-900 mb-6">{t('claims.createNewClaim')}</h1>
        
        {selectedPolicy && selectedClient && (
          <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6">
            <div className="px-4 py-5 sm:px-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                {t('claims.selectedPolicyAndClient')}
              </h3>
            </div>
            <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
              <dl className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
                <div className="sm:col-span-1">
                  <dt className="text-sm font-medium text-gray-500 flex items-center">
                    <ShieldCheckIcon className="h-4 w-4 mr-1 text-gray-400" /> {t('claims.policyNumber')}
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">{selectedPolicy.policyNumber}</dd>
                </div>
                <div className="sm:col-span-1">
                  <dt className="text-sm font-medium text-gray-500 flex items-center">
                    <ClipboardListIcon className="h-4 w-4 mr-1 text-gray-400" /> {t('claims.policyType')}
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">{selectedPolicy.policyType}</dd>
                </div>
                <div className="sm:col-span-1">
                  <dt className="text-sm font-medium text-gray-500 flex items-center">
                    <UserIcon className="h-4 w-4 mr-1 text-gray-400" /> {t('claims.clientName')}
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">{selectedClient.firstName} {selectedClient.lastName}</dd>
                </div>
                <div className="sm:col-span-1">
                  <dt className="text-sm font-medium text-gray-500 flex items-center">
                    <IdentificationIcon className="h-4 w-4 mr-1 text-gray-400" /> {t('claims.clientNumber')}
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">{selectedClient.clientNumber}</dd>
                </div>
                {selectedPolicy.policyDetails?.vehicleMake && (
                  <>
                    <div className="sm:col-span-1">
                      <dt className="text-sm font-medium text-gray-500">{t('policy.vehicle')}</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {selectedPolicy.policyDetails.vehicleMake} {selectedPolicy.policyDetails.vehicleModel} ({selectedPolicy.policyDetails.vehicleYear})
                      </dd>
                    </div>
                    <div className="sm:col-span-1">
                      <dt className="text-sm font-medium text-gray-500">{t('policy.licensePlate')}</dt>
                      <dd className="mt-1 text-sm text-gray-900">{selectedPolicy.policyDetails.vehiclePlate}</dd>
                    </div>
                  </>
                )}
              </dl>
            </div>
          </div>
        )}
        
        <form onSubmit={handleCreateClaim} className="space-y-6 max-w-2xl">
          {!policyId ? (
            <div>
              <label htmlFor="policyId" className="block text-sm font-medium text-gray-700">
                {t('claims.policy')}
              </label>
              <select
                id="policyId"
                name="policyId"
                value={newClaimData.policyId}
                onChange={handlePolicyChange}
                required
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
              >
                <option value="">{t('claims.selectPolicy')}</option>
                {policies.map((policy) => (
                  <option key={policy.id} value={policy.id}>
                    {policy.policyNumber} - {policy.policyType} ({t('claims.client')}: {policy.client?.firstName} {policy.client?.lastName})
                  </option>
                ))}
              </select>
            </div>
          ) : null}
          
          <div>
            <label htmlFor="outstandingAmount" className="block text-sm font-medium text-gray-700">
              {t('claims.outstandingAmount')} (LYD)
            </label>
            <input
              type="number"
              id="outstandingAmount"
              name="outstandingAmount"
              min="0"
              step="0.01"
              required
              value={newClaimData.outstandingAmount}
              onChange={(e) => setNewClaimData({...newClaimData, outstandingAmount: e.target.value})}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            />
          </div>
          
          <div>
            <label htmlFor="informedDate" className="block text-sm font-medium text-gray-700">
              {t('claims.informedDate')}
            </label>
            <input
              type="date"
              id="informedDate"
              name="informedDate"
              required
              value={newClaimData.informedDate}
              onChange={(e) => setNewClaimData({...newClaimData, informedDate: e.target.value})}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            />
          </div>
          
          <div>
            <label htmlFor="accidentDate" className="block text-sm font-medium text-gray-700">
              {t('claims.accidentDate')}
            </label>
            <input
              type="date"
              id="accidentDate"
              name="accidentDate"
              required
              value={newClaimData.accidentDate}
              onChange={(e) => setNewClaimData({...newClaimData, accidentDate: e.target.value})}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            />
          </div>
          
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700">
              {t('claims.description')}
            </label>
            <textarea
              id="description"
              name="description"
              rows={4}
              required
              value={newClaimData.description}
              onChange={(e) => setNewClaimData({...newClaimData, description: e.target.value})}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            />
          </div>
          
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => navigate('/claims')}
              className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              className="btn btn-primary"
            >
              {t('claims.submitClaim')}
            </button>
          </div>
        </form>
      </div>
    );
  }

  // Render claims list
  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center sm:justify-between">
        <h1 className="text-2xl font-semibold text-gray-900">{t('claims.title')}</h1>
        <button
          type="button"
          onClick={() => navigate('/claims/new')}
          className="btn btn-primary"
        >
          {t('claims.createNewClaim')}
        </button>
      </div>
      
      <div className="mt-4 sm:flex sm:items-center">
        <div className="relative flex-grow max-w-xs">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <SearchIcon className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder={t('claims.searchClaims')}
            className="input pl-10"
            onChange={(e) => console.log('Search not implemented')}
          />
        </div>
        
        <div className="mt-3 sm:mt-0 sm:ml-4">
          <select
            className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
            value={selectedStatus}
            onChange={handleStatusFilterChange}
          >
            <option value="">{t('claims.allStatuses')}</option>
            <option value="pending">{t('claims.pending')}</option>
            <option value="approved">{t('claims.approved')}</option>
            <option value="rejected">{t('claims.rejected')}</option>
            <option value="settled">{t('claims.settled')}</option>
          </select>
        </div>
      </div>
      
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <p>{t('claims.loadingClaims')}</p>
        </div>
      ) : (
        <>
          {claims.claims && claims.claims.length > 0 ? (
            <div className="mt-8 flow-root">
              <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                  <table className="min-w-full divide-y divide-gray-300">
                    <thead>
                      <tr>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          {t('claims.claimNumber')}
                        </th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          {t('claims.policy')}
                        </th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          {t('claims.client')}
                        </th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          {t('claims.amount')}
                        </th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          {t('claims.date')}
                        </th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          {t('common.status')}
                        </th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          {t('common.actions')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {claims.claims.map((claim) => (
                        <tr key={claim.id}>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {claim.claimNumber}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {claim.policy?.policyNumber}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {claim.client?.firstName} {claim.client?.lastName}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            LYD {claim.outstandingAmount}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {moment(claim.informedDate).format('DD/MM/YYYY')}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm">
                            <div className="flex items-center">
                              {getStatusIcon(claim.status)}
                              <span className="ml-1 capitalize">
                                {t(`claims.${claim.status}`)}
                              </span>
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            <div className="flex space-x-2">
                              {(user.role === 'admin' || user.role === 'company') && claim.status === 'pending' && (
                                <>
                                  <button
                                    onClick={() => handleUpdateClaimStatus(claim.id, 'approved')}
                                    className="text-green-600 hover:text-green-900 font-medium"
                                  >
                                    {t('claims.approve')}
                                  </button>
                                  <button
                                    onClick={() => handleUpdateClaimStatus(claim.id, 'rejected')}
                                    className="text-red-600 hover:text-red-900 font-medium"
                                  >
                                    {t('claims.reject')}
                                  </button>
                                </>
                              )}
                              
                              {(user.role === 'admin' || user.role === 'company') && claim.status === 'approved' && (
                                <button
                                  onClick={() => {
                                    const amount = prompt(t('claims.enterSettlementAmount'), claim.outstandingAmount);
                                    if (amount) {
                                      const today = moment().format('YYYY-MM-DD');
                                      handleUpdateClaimStatus(claim.id, 'settled', {
                                        settlementAmount: amount,
                                        settlementDate: today
                                      });
                                    }
                                  }}
                                  className="text-blue-600 hover:text-blue-900 font-medium"
                                >
                                  {t('claims.settle')}
                                </button>
                              )}
                              
                              <button
                                onClick={() => navigate(`/claims/${claim.id}`)}
                                className="text-indigo-600 hover:text-indigo-900 font-medium"
                              >
                                {t('common.view')}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : (
            <div className="mt-8 p-4 bg-white shadow rounded-lg text-center">
              <p className="text-gray-600">{t('claims.noClaims')}</p>
            </div>
          )}
          
          {claims.totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between">
              <button
                className="btn btn-primary"
                disabled={claims.currentPage === 1}
                onClick={() => fetchClaims(claims.currentPage - 1)}
              >
                {t('claims.previous')}
              </button>
              <span>
                {t('claims.page')} {claims.currentPage} {t('claims.of')} {claims.totalPages}
              </span>
              <button
                className="btn btn-primary"
                disabled={claims.currentPage === claims.totalPages}
                onClick={() => fetchClaims(claims.currentPage + 1)}
              >
                {t('claims.next')}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
} 